import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridViewWebComponent } from '../../../grid/gridView';
import { Column, ColumnKeys, DefaultViewProps, ErrorCodes, GridEntry, PhasrMonitoredRulesGridEvents } from '../types';
import { WEB_COMPONENT_GRID_VIEW, WEB_COMPONENT_GZ_TITLE } from '../../../webcomponents.constants';
import { translateFn } from '../../../utils/translator.helper';
import { GridConfig } from './GridConfig';
import { GridViewMetadata, SelectionStoreMode, ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import {
  behavioralProfilesRenderer,
  companyRenderer,
  recommendationsRenderer,
  restrictedBehavioralProfilesRenderer,
  ruleNameRenderer,
  ruleTriggerDateRenderer,
  ruleTriggersRenderer,
  selectColumnRenderer,
  targetedActivityTypeRenderer,
} from './customGridRenderers';
import { GzIconWebComponent } from '../../../ui/gzIcon';
import { ButtonColor } from '../../../ui/gzIconButton/types';
import { SideMenuWidthValues } from '../../../ui/gzSideMenu/types';
import { GzTextWebComponent } from '../../../ui/gzText';
import { PhasrMonitoredRulesDetailsPanelComponent } from '../../phasrMonitoredRulesDetailsPanel';
import moment from 'moment';
import { GzBackendError} from '../../../accounts/common/types/GzBackendError';
import { htmlEncode } from '../../../utils/escape.utils';
import { PhasrSidePanelComponent } from "../../../phasrSidePanel";
import { PhasrPanelComponentTypes, PhasrSidePanelEvents } from "../../../phasrSidePanel/types/types";
import { TemplateState } from '../../../types/common/types';
import { getTimezone } from '../../../utils/global.utils';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { ColumnKeys as RecommendationsColumnKeys } from '../../../phasrRecommendations/phasrRecommendationsGrid/types/grid';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { addCloseSideMenuFunctionality } from '../../common/helpers';
import {
  getPhasrCategoryDictionaryToPrefferedName,
} from '../../../types/common/phasrTypes';
import { Filter } from '../../../grid/gridFilters/types';
import { ALLOW_ACTION, RESTRICT_ACTION } from '../../../phasrRecommendations/phasrRecommendationsGrid/types';

export class PhasrMonitoredRulesGridView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $grid: GridViewWebComponent<Column, ColumnKeys, GridEntry>;
    $title: HTMLElement;
    $hostElement: HTMLElement;
  };
  private dictionary: any;

  public getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $grid: this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<Column, ColumnKeys>,
      $title: this.shadow.querySelector(WEB_COMPONENT_GZ_TITLE),
      $hostElement: this.shadow,
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  public setTranslatedStrings(): void {
    this.dom.$title.innerHTML = translateFn('PHASR monitored rules');
  }

  public async buildSuccessView(): Promise<void> {
    const { viewMetaData, defaultViewProps } = await new GridConfig().getDataForView(this.dependencies);

    this.buildGrid(viewMetaData, defaultViewProps);
  }

  public buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private buildGrid(viewMetaData: GridViewMetadata<Column, ColumnKeys>, defaultViewData: DefaultViewProps): void {
    const defaultViewProps: ViewConfiguration<Column, ColumnKeys> = {
      compactView: defaultViewData.compactView,
      displayedColumns: defaultViewData.displayedColumns,
      filters: defaultViewData.filters,
      name: '',
      pageSize: defaultViewData.pageSize,
      type: ViewType.DEFAULT,
      id: viewMetaData.viewId,
    };

    this.dictionary = Application.Main.getPhasrDictionary();

    this.dom.$grid.propertiesSet = false;
    this.dom.$grid.sideMenuParent = this.dom.$hostElement;
    this.dom.$grid.selectionStoreConfig = {
      functionalMode: SelectionStoreMode.RECORDS,
    };

    this.dom.$grid.customStrings = {
      noSearchResultsString: this.dependencies.translateFn('No monitored rules'),
    };
    for (const [key, value] of Object.entries(viewMetaData)) {
      this.dom.$grid[key] = value;
    }
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.RULE_NAME]: ruleNameRenderer.bind(this),
      [ColumnKeys.RECOMMENDATIONS]: recommendationsRenderer.bind(this),
      [ColumnKeys.RULE_TRIGGER_DATE]: ruleTriggerDateRenderer.bind(this),
      [ColumnKeys.TARGETED_ACTIVITY_TYPE]: targetedActivityTypeRenderer.bind(this),
      [ColumnKeys.RULE_TRIGGERS]: ruleTriggersRenderer.bind(this),
      [ColumnKeys.BEHAVIORAL_PROFILES]: behavioralProfilesRenderer.bind(this),
      [ColumnKeys.RESTRICTED_BEHAVIORAL_PROFILES]: restrictedBehavioralProfilesRenderer.bind(this),
      [ColumnKeys.COMPANY]: companyRenderer.bind(this),
      [ColumnKeys.ROW_ACTIONS]: selectColumnRenderer.bind(this),
    };

    this.dom.$grid.defaultViewProps = defaultViewProps;
    this.dom.$grid.onDataLoadingFinishedWithError = error => this.setGridErrorMessage(error);

    this.addPivotingFilterValues(defaultViewProps.filters);

    this.dom.$grid.propertiesSet = true;
    this.dom.$grid.injectDependencies(this.dependencies);
    this.dom.$grid.start().catch();
  }

  public async setGridErrorMessage(error: GzBackendError | undefined) {
    let dataLoadingFailedMessage = htmlEncode(
      translateFn(
        'Data loading has failed. Please try to refresh the page and if the issue persists contact Bitdefender Enterprise Support for more information.'
      )
    );

    if (error?.err?.code === ErrorCodes.COMPANY_DOES_NOT_EXIST_OR_IS_NOT_ACCESSIBLE) {
      dataLoadingFailedMessage = translateFn(
        'You have companies in your saved filters that do not exist or are not accessible anymore. Change your company filter and try again.'
      );
    }

    this.dom.$grid.customStrings.dataLoadingFailed = dataLoadingFailedMessage;
    this.dom.$grid.controller?.updateCustomStrings();
  }

  private getBehavioralProfilesPanel(data: GridEntry): PhasrSidePanelComponent {
    const sidePanelContent = new PhasrSidePanelComponent();
    sidePanelContent.titleText = translateFn('Behavioral profiles');
    sidePanelContent.componentType = PhasrPanelComponentTypes.BehavioralProfiles;
    sidePanelContent.companyId = data.companyId;
    sidePanelContent.componentSpecificData = {
      recommendationId: data.objectId
    }
    sidePanelContent.injectDependencies(this.dependencies);
    sidePanelContent.start().catch(console.error);
    return sidePanelContent;
  }

  private getRestrictedBehavioralProfilesPanel(data: GridEntry): PhasrSidePanelComponent {
    const sidePanelContent = new PhasrSidePanelComponent();
    sidePanelContent.titleText = translateFn('Restricted behavioral profiles');
    sidePanelContent.componentType = PhasrPanelComponentTypes.RestrictedBehavioralProfiles;
    sidePanelContent.companyId = data.companyId;
    sidePanelContent.componentSpecificData = {
      recommendationId: data.objectId,
      ruleId: data.ruleId,
      ruleTriggers: data.ruleTriggers,
      phasrUsable: data.phasrUsable,
    }
    sidePanelContent.injectDependencies(this.dependencies);
    sidePanelContent.start().catch(console.error);
    return sidePanelContent;
  }

  private getRuleTriggersPanel(data: GridEntry): PhasrSidePanelComponent {
    const sidePanelContent = new PhasrSidePanelComponent();
    sidePanelContent.titleText = translateFn('Rule triggers');
    sidePanelContent.componentType = PhasrPanelComponentTypes.RuleTriggers
    sidePanelContent.companyId = data?.companyId;
    sidePanelContent.componentSpecificData = {
      ruleId: data.ruleId
    }
    sidePanelContent.injectDependencies(this.dependencies);
    sidePanelContent.start().catch(console.error);
    return sidePanelContent;
  }

  public handleNavigateToRecommendationsClick(data: GridEntry): void {
    const params: {
      filters: Partial<Record<RecommendationsColumnKeys, unknown>>[];
    } = {
      filters: [
        {
          [RecommendationsColumnKeys.COMPANY]: {
            exactMatch: data.companyId,
          },
        },
        {
          [RecommendationsColumnKeys.RECOMMENDATION_NAME]: {
            complexEnumFilter: [
              {
                value: data[ColumnKeys.RULE_NAME] + '-' + RESTRICT_ACTION,
                label: this.dictionary[data[ColumnKeys.RULE_NAME]]?.rule_recommendation ?? 'N/A',
              },
              {
                value: data[ColumnKeys.RULE_NAME] + '-' + ALLOW_ACTION,
                label: this.dictionary[data[ColumnKeys.RULE_NAME]]?.rule_allow_name ?? 'N/A',
              },
            ]
          },
        },
        {
          [RecommendationsColumnKeys.CREATED_ON]: {
            timeRange: {
              kind: TimeRangeKind.ALL_TO_DATE,
            },
          }
        },
      ]
    };
    NavigationHelper.navigateTo('/phasr-recommendations', params);
  }

  private addPivotingFilterValues(defaultFilters: Filter<ColumnKeys>[]): void {
    if (!this.dependencies.navigationParams) {
      return;
    }

    const existingFilters = defaultFilters;

    for (const filter of this.dependencies.navigationParams.filters) {
      let keyExists = false;
      const filterKey = Object.keys(filter)[0];
      const filterValue = filter[filterKey];

      if (filterKey === ColumnKeys.COMPANY && !this.dependencies.permissions.canHaveChildCompanies) {
        continue;
      }

      for (const [index, existingFilter] of existingFilters.entries()) {
        if (Object.keys(existingFilter)[0] === filterKey) {
          existingFilters[index][filterKey] = filterValue;
          keyExists = true;
        }
      }

      if (!keyExists) {
        existingFilters.push(filter);
      }
    }

    this.dom.$grid.filters = existingFilters;

    this.dependencies.navigationParams = null;
  }

  public handlePhasrSidePanelOpen(phasrComponentType: PhasrPanelComponentTypes, currentTarget: HTMLElement, data: GridEntry): void {
    const phasrSidePanel = new GzSideMenuWebComponent();
    phasrSidePanel.isOpen = true;
    phasrSidePanel.closeOnClickOutside = true;
    phasrSidePanel.menuWidth = SideMenuWidthValues.MEDIUM;
    phasrSidePanel.setAttribute('id', phasrComponentType);
    phasrSidePanel.classList.add('phasr-side-panel');

    let phasrSidePanelContent;
    switch (phasrComponentType) {
      case PhasrPanelComponentTypes.BehavioralProfiles:
        this.updateActiveStateFromGzIcon(currentTarget, true);
        phasrSidePanelContent = this.getBehavioralProfilesPanel(data);
        break;
      case PhasrPanelComponentTypes.RestrictedBehavioralProfiles:
        this.updateActiveStateFromGzIcon(currentTarget, true);
        phasrSidePanelContent = this.getRestrictedBehavioralProfilesPanel(data);
        break;
      case PhasrPanelComponentTypes.RuleTriggers:
        phasrSidePanelContent = this.getRuleTriggersPanel(data);
        break;
      case PhasrPanelComponentTypes.MonitoredRulesDetails:
        phasrSidePanelContent = this.getMonitoredRulesDetailsPanel(data);
        break;
      default:
        break;
    }
    phasrSidePanel.setMenuContent(phasrSidePanelContent);
    phasrSidePanel.openSideMenu();
    phasrSidePanel.addEventListener('close', () => {
      phasrSidePanel.remove();
      if ([PhasrPanelComponentTypes.BehavioralProfiles, PhasrPanelComponentTypes.RestrictedBehavioralProfiles].includes(phasrComponentType)) {
        this.updateActiveStateFromGzIcon(currentTarget, false);
      }
    });
    phasrSidePanel.addEventListener(PhasrSidePanelEvents.EditAccess, (event: any) => {
      this.dom.$grid.dispatchEvent(
        new CustomEvent(PhasrMonitoredRulesGridEvents.OnEditAccessFromSidePanel, {
          detail: event.detail,
          bubbles: true,
          composed: true,
        })
      )
    });

    addCloseSideMenuFunctionality(phasrSidePanel);
  }

  public updateActiveStateFromGzIcon(currentTarget: HTMLElement, active: boolean): void {
    const gzIcon = currentTarget.querySelector('gz-icon') as GzIconWebComponent;
    gzIcon.color = active ? ButtonColor.PRIMARY : ButtonColor.SECONDARY;
    const gzText = currentTarget.querySelector('gz-text') as GzTextWebComponent;
    gzText.style.color = active ? 'var(--blue-layout-400-primary)' : 'var(--body-text-arrows-400)';
  }

  private getMonitoredRulesDetailsPanel(data: GridEntry): PhasrMonitoredRulesDetailsPanelComponent {

    let ruleName = 'N/A';
    let ruleDescription = 'N/A';
    let activityType = 'N/A';
    if (this.dictionary) {
      ruleDescription = this.dictionary[data.ruleId]?.rule_description ?? 'N/A';
      ruleName = this.dictionary[data.ruleId]?.rule_name ?? 'N/A';
      activityType = getPhasrCategoryDictionaryToPrefferedName()[this.dictionary[data.ruleId]?.['category']] ?? 'N/A';
    }
    const detailsPanelContent = new PhasrMonitoredRulesDetailsPanelComponent();
    detailsPanelContent.ruleName = ruleName;
    detailsPanelContent.generalDetails = {
      targetActivityType: activityType,
      ruleTriggerDate: data.ruleTriggerDate ? moment(data.ruleTriggerDate * 1000).tz(getTimezone()).format('DD MMM YYYY, HH:mm:ss') : '-',
      ruleTriggers: data.ruleTriggers,
      ruleId: data.ruleId,
      recommendationId: data.objectId,
      recommendations: data.recommendations,
      behavioralProfiles: data.behavioralProfiles,
      restrictedBehavioralProfiles: data.restrictedBehavioralProfiles,
      companyId: data.companyId,
      phasrUsable: data.phasrUsable,
    };

    detailsPanelContent.addEventListener(PhasrSidePanelEvents.EditAccess, (event: any) => {
      this.dom.$grid.dispatchEvent(
        new CustomEvent(PhasrMonitoredRulesGridEvents.OnEditAccessFromSidePanel, {
          detail: event.detail,
          bubbles: true,
          composed: true,
        })
      )
    });
    detailsPanelContent.monitoredRuleDescription = ruleDescription;
    detailsPanelContent.injectDependencies(this.dependencies);
    detailsPanelContent.start().then().catch();
    return detailsPanelContent;
  }

  public getCompanyIdFilterValue(): string {
    if (this.dependencies.permissions.canHaveChildCompanies) {
      const companyFilter = this.dom.$grid.filters.find(filter => ColumnKeys.COMPANY in filter);
      return companyFilter?.company?.exactMatch ?? '';
    } else {
      return '';
    }
  }
}
